import React from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/layout/PageLayout';
import { PermissionGuard } from '@/components/layout';
import Table from '@/components/ui/tables/Table';
import { IndicatorCard } from '@/components/ui/cards/IndicatorCard';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { DateRangeFilter } from '@/components/ui/filters/DateRangeFilter';
import { Selector } from '@/components/ui/inputs/selector';
import { formatDateToYYYYMMDD } from '@/utils';
import { useShiftChangeRequestsAdmin } from '@/hooks/attendance/management/useShiftChangeRequestsAdmin';
import { ShiftChangeRequestBulkApprovalModal } from '@/components/features/attendance/shiftChangeRequests/ShiftChangeRequestBulkApprovalModal';
import { ShiftChangeRequestBulkRejectionModal } from '@/components/features/attendance/shiftChangeRequests/ShiftChangeRequestBulkRejectionModal';


// NOTE: Los contadores KPI (ASIS-5237) reflejan el mismo conjunto filtrado del
// listado (estado incluido, ASIS-5231, y búsqueda libre) y se recalculan al cambiar los filtros.
export const ShiftChangeRequestsAdmin: React.FC = () => {
  const { t: translate } = useTranslation();
  const {
    rows,
    counters,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    columns,
    actions,
    onSearch,
    onSortChange,
    statusFilters,
    filterValues,
    onChangeFilter,
    dateRange,
    onDateRangeChange,
    collaboratorOptions,
    collaboratorValue,
    onCollaboratorChange,
    collaboratorOnLoadMore,
    collaboratorHasNextPage,
    collaboratorIsLoadingMore,
    collaboratorOnSearchChange,
    onExport,
    isExporting,
    bulkActions,
    tableSelectionKey,
    onSelectionChange,
    isRowSelectable,
    selectedRequests,
    isBulkApproveModalOpen,
    onBulkApproveModalClose,
    onBulkApproveSuccess,
    isBulkRejectModalOpen,
    onBulkRejectModalClose,
    onBulkRejectionSuccess,
  } = useShiftChangeRequestsAdmin();

  return (
    <PageLayout dynamicTitle={translate('attendance:shiftChangeAdmin.title')}>
      <div className="flex flex-col gap-4" data-testid="shift-change-requests-admin-page">
        {/*
          Contadores KPI por estado (ASIS-5559): según el diseño, Pendientes y Aprobados se
          muestran en azul y Rechazados en rojo. Los valores siguen los filtros vigentes.
        */}
        <div
          className="flex flex-row gap-4 overflow-x-auto pb-2"
          data-testid="shift-change-requests-admin-counters"
        >
          <IndicatorCard
            label={translate('attendance:shiftChangeAdmin.indicators.pending')}
            value={counters.pending}
            variant="important"
            data-testid="indicator-pending"
          />
          <IndicatorCard
            label={translate('attendance:shiftChangeAdmin.indicators.approved')}
            value={counters.approved}
            variant="important"
            data-testid="indicator-approved"
          />
          <IndicatorCard
            label={translate('attendance:shiftChangeAdmin.indicators.rejected')}
            value={counters.rejected}
            variant="error"
            data-testid="indicator-rejected"
          />
        </div>

        {/* Filtros + descarga, justo encima de la tabla */}
        <div
          className="flex flex-wrap gap-3 items-center justify-between"
          data-testid="shift-change-requests-admin-actions"
        >
          <div className="flex flex-wrap gap-3 items-center">
            <DateRangeFilter
              value={dateRange}
              onChange={onDateRangeChange}
              maxDate={formatDateToYYYYMMDD(new Date())}
              initialMonthOffset={-1}
              placeholder={translate('attendance:shiftChangeAdmin.filters.dateRange')}
              closeOnComplete
            />
            <FilterDropdown
              filters={statusFilters}
              values={filterValues}
              onChangeFilter={onChangeFilter}
              extraFilters={
                <Selector
                  label={translate('attendance:shiftChangeAdmin.filters.employee')}
                  placeholder={translate('attendance:shiftChangeAdmin.filters.allEmployees')}
                  options={collaboratorOptions}
                  value={collaboratorValue}
                  onChange={(val) =>
                    onCollaboratorChange(Array.isArray(val) ? (val[0] ?? '') : val)
                  }
                  onLoadMore={collaboratorOnLoadMore}
                  hasNextPage={collaboratorHasNextPage}
                  isLoadingMore={collaboratorIsLoadingMore}
                  onSearchChange={collaboratorOnSearchChange}
                  searchable
                  data-testid="shift-collaborator-selector"
                />
              }
            />
          </div>

          <PermissionGuard action="view" fallback="hidden">
            <ExportDropdown
              onExport={onExport}
              placement="right"
              availableFormats={['EXCEL']}
              isLoading={isExporting}
              disabled={total === 0}
            />
          </PermissionGuard>
        </div>

        <Table
          key={tableSelectionKey}
          columns={columns}
          actions={actions}
          data={rows}
          totalRows={total}
          isLoading={loading}
          searchable
          onSearch={onSearch}
          serverSideSort
          initialSort={{ key: 'requestDate', direction: 'desc' }}
          onSortChange={onSortChange}
          selectable
          onSelectionChange={onSelectionChange}
          isRowSelectable={isRowSelectable}
          bulkActions={bulkActions}
          pagination={{
            page,
            pageSize,
            total,
            onChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          data-testid="shift-change-requests-admin-table"
          emptyMessage={translate('attendance:shiftChangeAdmin.messages.noResults')}
        />
      </div>

      <PermissionGuard action="edit" fallback="hidden">
        <ShiftChangeRequestBulkApprovalModal
          isOpen={isBulkApproveModalOpen}
          selectedRequests={selectedRequests}
          onClose={onBulkApproveModalClose}
          onSuccess={onBulkApproveSuccess}
        />
        <ShiftChangeRequestBulkRejectionModal
          isOpen={isBulkRejectModalOpen}
          selectedRequests={selectedRequests}
          onClose={onBulkRejectModalClose}
          onSuccess={onBulkRejectionSuccess}
        />
      </PermissionGuard>
    </PageLayout>
  );
};

export default ShiftChangeRequestsAdmin;
